import { BaseTextFieldProps } from '@mui/material';
import { ChangeEvent, FocusEvent, useState } from 'react';
import { TextWrapperProps } from './TextWrapper';
import { clockTimePattern, timePattern } from '../../utils/regex';
import { completeClockTime } from '../../utils/completeClockTime';
import { clockTimeToMinutes } from '../../utils/clockTimeMinutes';
import { formatClockTime } from '../../utils/formatClockTime';
import { TextField } from './TextField';

type TextFieldClockProps = BaseTextFieldProps & {
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  wrapperProps: Omit<TextWrapperProps, 'children'>;
  name: string;
  value?: string;
  required?: boolean;
  error?: boolean;
  errorText?: string;
  disabled?: boolean;
  minTime?: string;
};

export const TextFieldClock = ({
  wrapperProps,
  onChange,
  name,
  value,
  required = false,
  error,
  errorText,
  disabled = false,
  minTime,
}: TextFieldClockProps) => {
  const [timeError, setTimeError] = useState(false);

  const valueMinutes = clockTimeToMinutes(value);
  const minTimeMinutes = clockTimeToMinutes(minTime);
  const minTimeError =
    valueMinutes !== null && minTimeMinutes !== null && valueMinutes < minTimeMinutes;

  const computedErrorText = timeError
    ? 'Ogiltigt klockformat'
    : minTimeError
      ? `Tiden kan inte vara tidigare än ${formatClockTime(minTime)}`
      : errorText;

  const handleTimeInputValueOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const timeInput = event.target.value;

    // Only characters that can never become a time are flagged while typing: a short form
    // passes through invalid steps (`164` on the way to `1645`) and is completed on blur.
    setTimeError(timeInput !== '' && !timePattern.test(timeInput));

    onChange(event);
  };

  const handleTimeInputOnBlur = (event: FocusEvent<HTMLInputElement>) => {
    const timeInput = event.target.value;
    const completed = completeClockTime(timeInput);

    setTimeError(completed !== '' && !clockTimePattern.test(completed));

    if (completed !== timeInput) {
      onChange({ target: { name, value: completed } } as ChangeEvent<HTMLInputElement>);
    }
  };

  return (
    <TextField
      name={name}
      value={value}
      disabled={disabled}
      onChange={handleTimeInputValueOnChange}
      onBlur={handleTimeInputOnBlur}
      error={error || timeError || minTimeError}
      errorText={computedErrorText}
      required={required}
      placeholder="HH:MM"
      inputProps={{ maxLength: 5 }}
      wrapperProps={wrapperProps}
    />
  );
};
